import axios from 'axios';

const QR_PENDING_KEY = 'ewu_qr_pending_submission';

function newUuid() {
    return typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-0000-4000-8000-${Math.random().toString(16).slice(2, 14)}`;
}

function readPendingSubmission() {
    try { return JSON.parse(sessionStorage.getItem(QR_PENDING_KEY) || 'null'); } catch { return null; }
}

function writePendingSubmission(value) {
    try {
        if (value) sessionStorage.setItem(QR_PENDING_KEY, JSON.stringify(value));
        else sessionStorage.removeItem(QR_PENDING_KEY);
    } catch { /* Polling still works in the current page. */ }
}

async function pollSubmission(context, pending) {
    context.qrSubmissionStatus = 'SENDING_TO_RESTAURANT';
    context.qrSubmissionMessage = 'Estamos enviando el pedido al restaurante. No cierres esta pantalla.';
    const deadline = new Date(pending.expires_at).getTime() + 5000;
    while (Date.now() < deadline) {
        const response = await axios.get(
            process.env.VUE_APP_API_URL + pending.status_url,
            { timeout: 8000 },
        );
        const status = response.data?.status;
        context.qrSubmissionStatus = status;
        if (status === 'ORDER_CONFIRMED') {
            context.qrSubmissionMessage = 'Pedido confirmado. El restaurante ya lo ha recibido.';
            writePendingSubmission(null);
            return { success: true, data: response.data };
        }
        if (status === 'ORDER_NOT_DELIVERED' || status === 'ORDER_REJECTED') {
            context.qrSubmissionMessage = status === 'ORDER_NOT_DELIVERED'
                ? 'No hemos podido confirmar la recepción. El pedido no se ha enviado al restaurante.'
                : 'El pedido no ha podido confirmarse. Revisa el carrito.';
            writePendingSubmission(null);
            const error = new Error(response.data?.failure_code || status);
            const failureDetails = response.data?.failure_details && typeof response.data.failure_details === 'object'
                ? response.data.failure_details
                : {};
            error.response = {
                status: 409,
                data: { ...response.data, ...failureDetails },
            };
            return { success: false, error };
        }
        await new Promise((resolve) => setTimeout(resolve, pending.retry_after_ms || 1000));
    }
    const error = new Error('QR_DELIVERY_TIMEOUT');
    error.response = { status: 409, data: { code: 'QR_DELIVERY_TIMEOUT' } };
    return { success: false, error };
}

/**
 * Handles error responses for order-related API calls (create_order, checkCanPay...)
 * @param {Object} context - The Vue component instance holding the error state flags
 * @param {Object} error - The axios error object
 */
export const processOrderError = (context, error) => {
    if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data || {};
        const failureDetails = errorData.failure_details && typeof errorData.failure_details === 'object'
            ? errorData.failure_details
            : {};
        const normalizedErrorData = { ...errorData, ...failureDetails };
        const isStockError = status === 409 && (
            errorData.error === "stock_unavailable" ||
            errorData.code === "INGREDIENT_STOCK_UNAVAILABLE" ||
            errorData.failure_code === "INGREDIENT_STOCK_UNAVAILABLE" ||
            failureDetails.code === "INGREDIENT_STOCK_UNAVAILABLE"
        );

        if (status === 403) {
            if (errorData.error === "delivery") context.is_delivery_popup = true;
            else if (errorData.error === "take_away") context.is_take_away_popup = true;
            else if (errorData.error === "counter") context.is_counter_popup = true;
            else if (errorData.error === "kiosk") context.is_kiosk_popup = true;
        } else if (isStockError) {
            context.markStockUnavailable?.(normalizedErrorData);
            context.fetchShoppingCart?.();
        } else if (status === 409 && errorData.code === "payment_required") {
            // El restaurante cobra al pedir: se pasa a «Pedir y Pagar».
            if ('paymentRequiredByServer' in context) context.paymentRequiredByServer = true;
            context.errorMessage = errorData.error || "Este restaurante cobra al pedir. Pulsa «Pedir y Pagar».";
            context.showError = true;
            context.$emit?.('ReviseSession');
            setTimeout(() => { context.showError = false; context.errorMessage = ''; }, 5000);
        } else if (status === 409 && errorData.code && errorData.error && errorData.code !== "QR_DELIVERY_TIMEOUT") {
            context.errorMessage = errorData.error;
            context.showError = true;
            context.fetchShoppingCart?.();
            setTimeout(() => { context.showError = false; context.errorMessage = ''; }, 5000);
        } else if (status === 409 && errorData.code === "bdp_service_busy") {
            context.errorMessage = errorData.error || "Servicio ocupado por ordenador central, en breves la liberaremos y podrás pedir";
            context.showError = true;
            setTimeout(() => { context.showError = false; context.errorMessage = ''; }, 5000);
        } else if (status === 409) {
            context.showDeliveryError = true;
            setTimeout(() => { context.showDeliveryError = false; }, 2000);
        } else if (status === 418) {
            context.showClosedKitchetError = true;
            setTimeout(() => { context.showClosedKitchetError = false; }, 5000);
        } else if (status === 401) {
            context.showRestaurantClosedError = true;
            setTimeout(() => { context.showRestaurantClosedError = false; }, 5000);
        } else {
            context.showError = true;
            setTimeout(() => { context.showError = false; }, 5000);
        }
    } else {
        context.showError = true;
        setTimeout(() => { context.showError = false; }, 5000);
    }
};

/**
 * Common handler for create_order API call and its error states.
 * @param {Object} context - The Vue component instance (this)
 * @param {Object} params - Optional reward_id and plate_reward_id
 */
export const handleCreateOrder = async (context, params = {}) => {
    const legacyUrl = process.env.VUE_APP_API_URL + '/v1/api/app/create_order';

    // Construct query parameters
    const queryParams = [];
    if (params.reward_id) queryParams.push(`reward_id=${params.reward_id}`);
    if (params.plate_reward_id) queryParams.push(`plate_reward_id=${params.plate_reward_id}`);
    if (params.frequent_customer_nif) queryParams.push(`frequent_customer_nif=${encodeURIComponent(params.frequent_customer_nif)}`);

    const fullUrl = queryParams.length > 0 ? `${legacyUrl}?${queryParams.join('&')}` : legacyUrl;

    try {
        let pending = readPendingSubmission();
        if (!pending) {
            const submissionUuid = newUuid();
            const response = await axios.post(
                process.env.VUE_APP_API_URL + '/v1/api/public/qr-orders/submissions',
                {
                    submission_uuid: submissionUuid,
                    idempotency_key: `qr-order:${submissionUuid}`,
                    customer_session_uuid: newUuid(),
                    ...params,
                },
                { headers: { 'Authorization': `Bearer ${context.token}` }, timeout: 10000 },
            );
            pending = {
                ...response.data,
                status_url: response.data.status_url,
            };
            writePendingSubmission(pending);
        }
        return await pollSubmission(context, pending);
    } catch (qrError) {
        const qrCode = qrError?.response?.data?.code;
        if (qrCode === 'QR_CLOUD_ONLY') {
            // The ACK endpoint deliberately hands cloud-only restaurants
            // back to the legacy cloud order flow. Keep the already-open
            // cart in sync so an old QR availability value does not leave a
            // misleading "temporarily unavailable" banner on screen.
            const cloudOnlyAvailability = {
                ...(context.qrAvailability || {}),
                available: true,
                status: qrCode,
            };
            // Vue exposes component data through a proxy, so hasOwnProperty is
            // not reliable here. The assignment must also clear an old
            // QR_NO_ACTIVE_RECEIVER value already rendered by Carrito.
            if (context && 'qrAvailability' in context) {
                context.qrAvailability = cloudOnlyAvailability;
            }
            try {
                sessionStorage.setItem(
                    'ewu_qr_availability',
                    JSON.stringify(cloudOnlyAvailability),
                );
            } catch {
                // sessionStorage can be unavailable in privacy mode; the
                // cloud fallback itself must still continue.
            }
        }
        if (!['QR_NOT_ENABLED', 'QR_CLOUD_ONLY'].includes(qrCode)) {
            console.log("Error en qr order submission", qrError);
            processOrderError(context, qrError);
            return { success: false, error: qrError };
        }
    }

    try {
        const response = await axios.post(fullUrl, {}, {
            headers: {
                'Authorization': `Bearer ${context.token}`
            }
        });
        return { success: true, data: response.data };
    } catch (error) {
        console.log("Error en create_order", error);
        processOrderError(context, error);
        return { success: false, error };
    }
};
